import React, { useState } from 'react';
import { FilterIcon, DownloadIcon, SearchIcon, MonitorIcon, SmartphoneIcon, TabletIcon, LogOutIcon, ClockIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Table from '../../components/ui/Table';
import StatusBadge from '../../components/ui/StatusBadge';
import DateRangePicker from '../../components/ui/DateRangePicker';
import ExportMenu from '../../components/ui/ExportMenu';
import Modal from '../../components/ui/Modal';
// Mock session history data
const sessionHistoryData = [{
  id: '1',
  loginTime: '2023-07-15T08:30:00Z',
  logoutTime: '2023-07-15T10:45:00Z',
  userId: 'U1023',
  userName: 'John Smith',
  role: 'Admin',
  ipAddress: '192.168.1.1',
  deviceType: 'web',
  browserInfo: 'Chrome 112',
  sessionStatus: 'ended',
  duration: '2h 15m'
}, {
  id: '2',
  loginTime: '2023-07-15T09:15:00Z',
  logoutTime: null,
  userId: 'U1024',
  userName: 'Jane Doe',
  role: 'Manager',
  ipAddress: '192.168.1.2',
  deviceType: 'mobile',
  browserInfo: 'App v1.3.2',
  sessionStatus: 'active',
  duration: 'Active'
}, {
  id: '3',
  loginTime: '2023-07-15T10:00:00Z',
  logoutTime: '2023-07-15T10:15:00Z',
  userId: 'U1025',
  userName: 'Robert Johnson',
  role: 'Agent',
  ipAddress: '192.168.1.3',
  deviceType: 'tablet',
  browserInfo: 'Safari iOS 16',
  sessionStatus: 'timed_out',
  duration: '15m'
}, {
  id: '4',
  loginTime: '2023-07-15T11:30:00Z',
  logoutTime: '2023-07-15T14:45:00Z',
  userId: 'U1026',
  userName: 'Sarah Williams',
  role: 'Auditor',
  ipAddress: '192.168.1.4',
  deviceType: 'web',
  browserInfo: 'Firefox 102',
  sessionStatus: 'ended',
  duration: '3h 15m'
}, {
  id: '5',
  loginTime: '2023-07-15T12:45:00Z',
  logoutTime: '2023-07-15T13:00:00Z',
  userId: 'U1027',
  userName: 'Michael Brown',
  role: 'Admin',
  ipAddress: '192.168.1.5',
  deviceType: 'web',
  browserInfo: 'Edge 112',
  sessionStatus: 'terminated',
  duration: '15m'
}, {
  id: '6',
  loginTime: '2023-07-15T13:30:00Z',
  logoutTime: null,
  userId: 'U1028',
  userName: 'Emily Davis',
  role: 'Manager',
  ipAddress: '192.168.1.6',
  deviceType: 'mobile',
  browserInfo: 'App v1.3.2',
  sessionStatus: 'active',
  duration: 'Active'
}, {
  id: '7',
  loginTime: '2023-07-15T14:15:00Z',
  logoutTime: '2023-07-15T16:30:00Z',
  userId: 'U1029',
  userName: 'David Wilson',
  role: 'Checker',
  ipAddress: '192.168.1.7',
  deviceType: 'web',
  browserInfo: 'Chrome 112',
  sessionStatus: 'ended',
  duration: '2h 15m'
}, {
  id: '8',
  loginTime: '2023-07-15T15:00:00Z',
  logoutTime: null,
  userId: 'U1030',
  userName: 'Linda Miller',
  role: 'Maker',
  ipAddress: '192.168.1.8',
  deviceType: 'web',
  browserInfo: 'Safari 16',
  sessionStatus: 'active',
  duration: 'Active'
}, {
  id: '9',
  loginTime: '2023-07-15T16:45:00Z',
  logoutTime: '2023-07-15T17:00:00Z',
  userId: 'U1031',
  userName: 'James Taylor',
  role: 'Admin',
  ipAddress: '192.168.1.9',
  deviceType: 'tablet',
  browserInfo: 'Safari iOS 16',
  sessionStatus: 'timed_out',
  duration: '15m'
}, {
  id: '10',
  loginTime: '2023-07-15T17:30:00Z',
  logoutTime: null,
  userId: 'U1032',
  userName: 'Patricia Anderson',
  role: 'Checker',
  ipAddress: '192.168.1.10',
  deviceType: 'web',
  browserInfo: 'Chrome 112',
  sessionStatus: 'active',
  duration: 'Active'
}];
interface SessionHistory {
  id: string;
  loginTime: string;
  logoutTime: string | null;
  userId: string;
  userName: string;
  role: string;
  ipAddress: string;
  deviceType: string;
  browserInfo: string;
  sessionStatus: string;
  duration: string;
}
const SessionHistory: React.FC = () => {
  const [sessions, setSessions] = useState<SessionHistory[]>(sessionHistoryData);
  const [filteredSessions, setFilteredSessions] = useState<SessionHistory[]>(sessionHistoryData);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSession, setSelectedSession] = useState<SessionHistory | null>(null);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [terminationReason, setTerminationReason] = useState('');
  // Filter states
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [userFilter, setUserFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deviceFilter, setDeviceFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');
  const handleTerminateSession = (session: SessionHistory) => {
    setSelectedSession(session);
    setShowTerminateModal(true);
  };
  const confirmTerminate = () => {
    if (selectedSession) {
      // In a real application, this would call an API to terminate the session
      const updatedSessions = sessions.map(session => {
        if (session.id === selectedSession.id) {
          return {
            ...session,
            logoutTime: new Date().toISOString(),
            sessionStatus: 'terminated',
            duration: calculateDuration(new Date(session.loginTime), new Date())
          };
        }
        return session;
      });
      setSessions(updatedSessions);
      setFilteredSessions(updatedSessions);
      setShowTerminateModal(false);
      setSelectedSession(null);
      setTerminationReason('');
    }
  };
  const handleExport = (format: 'csv' | 'xlsx' | 'pdf') => {
    console.log(`Exporting session history as ${format}`);
    // Implement export logic here
  };
  const applyFilters = () => {
    let filtered = [...sessions];
    if (startDate) {
      filtered = filtered.filter(session => new Date(session.loginTime) >= startDate);
    }
    if (endDate) {
      filtered = filtered.filter(session => {
        if (session.logoutTime) {
          return new Date(session.logoutTime) <= endDate;
        }
        return true; // Include active sessions
      });
    }
    if (userFilter) {
      filtered = filtered.filter(session => session.userName.toLowerCase().includes(userFilter.toLowerCase()) || session.userId.toLowerCase().includes(userFilter.toLowerCase()));
    }
    if (roleFilter) {
      filtered = filtered.filter(session => session.role === roleFilter);
    }
    if (statusFilter) {
      filtered = filtered.filter(session => session.sessionStatus === statusFilter);
    }
    if (deviceFilter) {
      filtered = filtered.filter(session => session.deviceType === deviceFilter);
    }
    if (ipFilter) {
      filtered = filtered.filter(session => session.ipAddress.includes(ipFilter));
    }
    setFilteredSessions(filtered);
  };
  const resetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setUserFilter('');
    setRoleFilter('');
    setStatusFilter('');
    setDeviceFilter('');
    setIpFilter('');
    setFilteredSessions(sessions);
  };
  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const calculateDuration = (start: Date, end: Date) => {
    const diffMs = end.getTime() - start.getTime();
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor(diffMs % (1000 * 60 * 60) / (1000 * 60));
    if (diffHrs > 0) {
      return `${diffHrs}h ${diffMins}m`;
    }
    return `${diffMins}m`;
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="success" text="Active" data-id="element-7805" />;
      case 'ended':
        return <StatusBadge status="default" text="Ended" data-id="element-7806" />;
      case 'timed_out':
        return <StatusBadge status="warning" text="Timed Out" data-id="element-7807" />;
      case 'terminated':
        return <StatusBadge status="danger" text="Terminated" data-id="element-7808" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-7809" />;
    }
  };
  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType) {
      case 'web':
        return <MonitorIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" data-id="element-7810" />;
      case 'mobile':
        return <SmartphoneIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" data-id="element-7811" />;
      case 'tablet':
        return <TabletIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" data-id="element-7812" />;
      default:
        return <MonitorIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" data-id="element-7813" />;
    }
  };
  // Get unique values for filters
  const uniqueRoles = Array.from(new Set(sessions.map(session => session.role)));
  const uniqueStatuses = Array.from(new Set(sessions.map(session => session.sessionStatus)));
  const uniqueDeviceTypes = Array.from(new Set(sessions.map(session => session.deviceType)));
  const columns = [{
    header: 'Login Time',
    accessor: (session: SessionHistory) => formatDateTime(session.loginTime),
    sortable: true
  }, {
    header: 'Logout Time',
    accessor: (session: SessionHistory) => formatDateTime(session.logoutTime),
    sortable: true
  }, {
    header: 'Duration',
    accessor: 'duration',
    sortable: true
  }, {
    header: 'User',
    accessor: (session: SessionHistory) => <div data-id="element-7814">
          <div className="font-medium" data-id="element-7815">{session.userName}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-7816">
            {session.userId}
          </div>
        </div>,
    sortable: false
  }, {
    header: 'Role',
    accessor: 'role',
    sortable: true
  }, {
    header: 'IP Address',
    accessor: 'ipAddress',
    sortable: true
  }, {
    header: 'Device / Browser',
    accessor: (session: SessionHistory) => <div className="flex items-center" data-id="element-7817">
          {getDeviceIcon(session.deviceType)}
          <span className="ml-2" data-id="element-7818">{session.browserInfo}</span>
        </div>,
    sortable: false
  }, {
    header: 'Status',
    accessor: (session: SessionHistory) => getStatusBadge(session.sessionStatus),
    sortable: false
  }];
  return <div className="space-y-6" data-id="element-7819">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-7820">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-7821">
          Session History
        </h1>
        <div className="mt-3 sm:mt-0 flex flex-wrap gap-2" data-id="element-7822">
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7823">
            <FilterIcon className="h-4 w-4 mr-2" data-id="element-7824" />
            Filters
          </button>
          <button onClick={() => {
          setSessions(sessionHistoryData);
          setFilteredSessions(sessionHistoryData);
        }} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7825">
            <div className="h-4 w-4 mr-2" data-id="element-7826" />
            Refresh
          </button>
          <ExportMenu onExport={handleExport} data-id="element-7827" />
        </div>
      </div>

      {/* Active Sessions Summary */}
      <Card data-id="element-7828">
        <div className="flex items-center mb-4" data-id="element-7829">
          <ClockIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-7830" />
          <h3 className="font-medium" data-id="element-7831">Active Sessions</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-7832">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-md p-4" data-id="element-7833">
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-7834">
              Total Active
            </div>
            <div className="text-2xl font-semibold text-gray-900 dark:text-white mt-1" data-id="element-7835">
              {sessions.filter(s => s.sessionStatus === 'active').length}
            </div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-md p-4" data-id="element-7836">
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-7837">
              Web Sessions
            </div>
            <div className="text-2xl font-semibold text-gray-900 dark:text-white mt-1" data-id="element-7838">
              {sessions.filter(s => s.sessionStatus === 'active' && s.deviceType === 'web').length}
            </div>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-md p-4" data-id="element-7839">
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-7840">
              Mobile Sessions
            </div>
            <div className="text-2xl font-semibold text-gray-900 dark:text-white mt-1" data-id="element-7841">
              {sessions.filter(s => s.sessionStatus === 'active' && s.deviceType === 'mobile').length}
            </div>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800 rounded-md p-4" data-id="element-7842">
            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-7843">
              Admin Sessions
            </div>
            <div className="text-2xl font-semibold text-gray-900 dark:text-white mt-1" data-id="element-7844">
              {sessions.filter(s => s.sessionStatus === 'active' && s.role === 'Admin').length}
            </div>
          </div>
        </div>
      </Card>

      {showFilters && <Card data-id="element-7845">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-7846">
            <div className="md:col-span-3" data-id="element-7847">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7848">
                Date Range
              </label>
              <DateRangePicker startDate={startDate} endDate={endDate} onStartDateChange={setStartDate} onEndDateChange={setEndDate} data-id="element-7849" />
            </div>
            <div data-id="element-7850">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7851">
                User / User ID
              </label>
              <div className="relative" data-id="element-7852">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-7853">
                  <SearchIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" data-id="element-7854" />
                </div>
                <input type="text" className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full pl-10 p-2.5" placeholder="Search user or ID" value={userFilter} onChange={e => setUserFilter(e.target.value)} data-id="element-7855" />
              </div>
            </div>
            <div data-id="element-7856">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7857">
                Role
              </label>
              <select className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full p-2.5" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} data-id="element-7858">
                <option value="" data-id="element-7859">All Roles</option>
                {uniqueRoles.map(role => <option key={role} value={role} data-id="element-7860">
                    {role}
                  </option>)}
              </select>
            </div>
            <div data-id="element-7861">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7862">
                Session Status
              </label>
              <select className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full p-2.5" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-7863">
                <option value="" data-id="element-7864">All Statuses</option>
                {uniqueStatuses.map(status => <option key={status} value={status} data-id="element-7865">
                    {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                  </option>)}
              </select>
            </div>
            <div data-id="element-7866">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7867">
                Device Type
              </label>
              <select className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full p-2.5" value={deviceFilter} onChange={e => setDeviceFilter(e.target.value)} data-id="element-7868">
                <option value="" data-id="element-7869">All Devices</option>
                {uniqueDeviceTypes.map(deviceType => <option key={deviceType} value={deviceType} data-id="element-7870">
                    {deviceType.charAt(0).toUpperCase() + deviceType.slice(1)}
                  </option>)}
              </select>
            </div>
            <div data-id="element-7871">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7872">
                IP Address
              </label>
              <input type="text" className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-md block w-full p-2.5" placeholder="e.g. 192.168.1.1" value={ipFilter} onChange={e => setIpFilter(e.target.value)} data-id="element-7873" />
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3" data-id="element-7874">
            <button onClick={resetFilters} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7875">
              Reset
            </button>
            <button onClick={applyFilters} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-7876">
              Apply Filters
            </button>
          </div>
        </Card>}

      <Card data-id="element-7877">
        <Table columns={columns} data={filteredSessions} keyField="id" actions={session => <div className="flex justify-end" data-id="element-7879">
              {session.sessionStatus === 'active' && <button onClick={() => handleTerminateSession(session)} className="p-1 rounded-full text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30" title="Terminate Session" data-id="element-7880">
                  <LogOutIcon className="h-5 w-5" data-id="element-7881" />
                </button>}
            </div>} data-id="element-7878" />
      </Card>

      {/* Terminate Session Modal */}
      <Modal isOpen={showTerminateModal} onClose={() => setShowTerminateModal(false)} title="Terminate Session" footer={<>
            <button onClick={() => setShowTerminateModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-7883">
              Cancel
            </button>
            <button onClick={confirmTerminate} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-7884">
              Terminate
            </button>
          </>} data-id="element-7882">
        {selectedSession && <div className="space-y-4" data-id="element-7885">
            <p className="text-gray-700 dark:text-gray-300" data-id="element-7886">
              Are you sure you want to terminate the active session for{' '}
              <span className="font-semibold" data-id="element-7887">{selectedSession.userName}</span>?
            </p>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 text-sm text-yellow-800 dark:text-yellow-300" data-id="element-7888">
              The user will be logged out immediately and will need to log in
              again.
            </div>
            <div data-id="element-7889">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-7890">
                Reason for Termination
              </label>
              <textarea rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter reason for terminating this session" value={terminationReason} onChange={e => setTerminationReason(e.target.value)} data-id="element-7891" />
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-3" data-id="element-7892">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-7893">
                Session Information
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-7894">
                <div data-id="element-7895">
                  <span className="font-medium" data-id="element-7896">Login Time:</span>{' '}
                  {formatDateTime(selectedSession.loginTime)}
                </div>
                <div data-id="element-7897">
                  <span className="font-medium" data-id="element-7898">IP Address:</span>{' '}
                  {selectedSession.ipAddress}
                </div>
                <div data-id="element-7899">
                  <span className="font-medium" data-id="element-7900">Device:</span>{' '}
                  {selectedSession.deviceType}
                </div>
                <div data-id="element-7901">
                  <span className="font-medium" data-id="element-7902">Browser:</span>{' '}
                  {selectedSession.browserInfo}
                </div>
              </div>
            </div>
          </div>}
      </Modal>
    </div>;
};
export default SessionHistory;